import type { PropsWithChildren } from "react";

import { SpinningLoader } from "@/components/SpinningLoader";
import { useTheme } from "@/components/ui/theme-provider";
import { useAuth } from "@/hooks/use-auth";
import { useDelayedLoader } from "@/hooks/use-delayed-loader";
import { cn } from "@/lib/utils";

export function GlobalLoader({ children }: Readonly<PropsWithChildren>) {
  const { isLoading } = useAuth();
  // NOTE: NSAI-5822 — the app also waits for the account's saved theme on a
  // browser that has never seen it, or last saw another account's, and for the
  // organization's branding CSS, which decides whether the sidebar keeps its
  // NodeShift tint. The boot otherwise guesses the scheme from the OS (or keeps
  // the previous account's) and paints the default tint, so an account set to
  // dark on a light machine painted a full white screen for the length of the
  // preferences request and then flipped — the "screen jumping on first load".
  // ThemeProvider bounds both waits, so a stalled request costs a beat rather
  // than the session.
  const { isThemeSettled } = useTheme();
  const isBooting = isLoading || !isThemeSettled;
  const isLoaderVisible = useDelayedLoader({ isLoading: isBooting });

  // NOTE: NSAI-6259 — the boot NEVER renders nothing. This used to return
  // `null` for the first 200ms of the wait, so a boot that ran long opened on a
  // blank window with no sign anything was happening ("2 seconds without a
  // loader, was it lost?"). The themed backdrop is now painted for the WHOLE
  // wait and only the spinner keeps the anti-flash delay, fading in rather
  // than appearing — which is what the delay was protecting against in the
  // first place. A boot that settles inside 200ms still never shows a spinner.
  //
  // The backdrop resolves through whatever scheme the document currently
  // carries, which before `isThemeSettled` is the boot's guess. That is
  // deliberate and is the line NSAI-5822 drew: the loader may show in the
  // guessed scheme, the app itself may not. See index.html for the pre-React
  // half of the same gap.
  if (isBooting) {
    return (
      <div
        className="flex h-screen w-full bg-background-secondary items-center justify-center"
        data-testid="global-loader"
        data-spinner-visible={isLoaderVisible}
      >
        <div
          className={cn(
            "transition-opacity duration-200 ease-out motion-reduce:transition-none",
            isLoaderVisible ? "opacity-100" : "opacity-0",
          )}
        >
          <SpinningLoader />
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
